/**
 * Markdown processing utilities
 * Handles image width syntax and other markdown transformations
 */

/**
 * Transform markdown width syntax from external to internal format
 * ![alt](url){width=300} → ![alt](url{width=300})
 * This embeds width in URL for extraction by img component
 */
export function processMarkdownForSizing(markdown: string): string {
  return markdown.replace(
    /!\[([^\]]*)\]\(([^)]+)\)\{width=(\d+(?:%|px)?)\}/g,
    (match, alt, src, width) => {
      // Clean alt text from any existing width syntax
      const cleanAlt = alt.replace(/\{width=\d+(?:%|px)?\}/, '').trim();

      // Append width marker to the URL so img handler can extract it
      return `![${cleanAlt}](${src}{width=${width}})`;
    }
  );
}

/**
 * Extract image width from markdown content for a specific image URL
 */
export function extractImageWidth(
  markdown: string,
  imageUrl?: string
): string | undefined {
  if (!imageUrl) return undefined;

  // Match markdown image syntax with optional width
  const imageMatch = markdown.match(
    /!\[([^\]]*)\]\(([^)]+)\)(?:\{width=(\d+(?:%|px)?)\})?/
  );

  if (imageMatch) {
    const matchedUrl = imageMatch[2];
    const width = imageMatch[3];

    // If the image URL matches and has width, return it
    if (matchedUrl === imageUrl && width) {
      return width;
    }
  }

  return undefined;
}

/**
 * Extract all tag mentions from markdown content
 * Matches @tag-slug pattern (e.g., @abstract, @lil-pudgys)
 * @returns Array of unique tag slugs mentioned in the content
 */
export function extractTagMentions(markdown: string): string[] {
  if (!markdown) return [];

  const mentionRegex = /@([a-z0-9]+(?:-[a-z0-9]+)*)/g;
  const mentions = new Set<string>();

  let match;
  while ((match = mentionRegex.exec(markdown)) !== null) {
    mentions.add(match[1]);
  }

  return Array.from(mentions);
}

/**
 * Parsed text segment - plain text, @mention, or $symbol
 */
export interface ParsedTextSegment {
  type: 'text' | 'mention' | 'symbol';
  value: string;
}

/**
 * Parse text content into segments of plain text, @mentions, and $symbols
 * Used for rendering inline links in React components
 * - @handle → links to entity glossary page
 * - $SYMBOL → links to token page (via linkedAsset lookup)
 * @param text - Text content to parse
 * @returns Array of text segments with type information
 */
export function parseTextWithMentions(text: string): ParsedTextSegment[] {
  if (!text) return [];

  // Combined regex for @mentions and $symbols
  // @mention: lowercase alphanumeric with hyphens
  // $SYMBOL: uppercase alphanumeric (2-10 chars, typical token symbols)
  const combinedRegex = /@([a-z0-9]+(?:-[a-z0-9]+)*)|\$([A-Z0-9]{2,10})/g;
  const segments: ParsedTextSegment[] = [];
  let lastIndex = 0;

  let match;
  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        value: text.substring(lastIndex, match.index)
      });
    }

    // Determine if this is a @mention or $symbol
    if (match[1]) {
      // @mention (group 1)
      segments.push({
        type: 'mention',
        value: match[1]
      });
    } else if (match[2]) {
      // $symbol (group 2)
      segments.push({
        type: 'symbol',
        value: match[2]
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      value: text.substring(lastIndex)
    });
  }

  return segments;
}

/**
 * Detect a Twitter/X profile link and extract the bare handle.
 *
 * Draft generators emit inline markdown links like `[@bunya_eth](https://x.com/bunya_eth)`
 * straight into body text. Those render as plain `<a>` tags via the markdown `a:`
 * renderer and NEVER reach the @mention parser (which only fires on literal
 * `@handle` text tokens). This helper lets the readers catch a profile-link href
 * and route it back through the entity-chip path.
 *
 * Matches profile URLs only (twitter.com/<handle> or x.com/<handle>) — NOT status
 * /tweet URLs (those are handled separately as TwitterEmbeds) and NOT reserved
 * paths like /home, /i/, /intent/, /search, /hashtag.
 *
 * @param href A URL string (or undefined).
 * @returns The lowercased handle (no @) if the href is a profile link, else null.
 */
export function extractTwitterHandleFromHref(href?: string): string | null {
  if (!href) return null;

  const match = href.match(
    /^https?:\/\/(?:www\.|mobile\.)?(?:twitter\.com|x\.com)\/(@?[A-Za-z0-9_]{1,15})\/?(?:\?.*)?$/i
  );
  if (!match) return null;

  const handle = match[1].replace(/^@/, '').toLowerCase();
  if (!handle) return null;

  // Reserved / non-profile first path segments.
  const reserved = new Set([
    'home', 'explore', 'notifications', 'messages', 'search',
    'settings', 'i', 'intent', 'hashtag', 'compose', 'share', 'login', 'signup',
  ]);
  if (reserved.has(handle)) return null;

  return handle;
}

/**
 * Extract all token symbols from markdown content
 * Matches $SYMBOL pattern (e.g., $PENGU, $ETH, $BTC)
 * @returns Array of unique symbols mentioned (without $ prefix)
 */
export function extractTokenSymbols(markdown: string): string[] {
  if (!markdown) return [];

  const symbolRegex = /\$([A-Z0-9]{2,10})/g;
  const symbols = new Set<string>();

  let match;
  while ((match = symbolRegex.exec(markdown)) !== null) {
    symbols.add(match[1]);
  }

  return Array.from(symbols);
}
